/**
 * Tenant Membership Stats report reads + rebuild (WSRADM-0021).
 *
 * Bare `/admin/reports/**` paths so `resolveBaseUrl` uses `apiBase()` (`/api`).
 * Tenant is the JWT — never a slug on the path, query, or header. Bearer only;
 * no cookies, no CSRF. Never `/admin/platform/**`. Empty-string query keys are
 * omitted. `state` is always sent on the members GET.
 */
import { request } from '@/api/client';
import { ApiError } from '@/api/errors';
import { parseTriggerJobResponse } from '@/api/triggerJob';
import type { TriggerJobResponse } from '@/api/triggerJob';
import {
  parseMembershipFamiliesResponse,
  parseMembershipMembersPage,
  parseMembershipMonthDetail,
  parseMembershipMonthsPage,
  parseRevenueLinesPage,
  parseRevenueMembersPage,
  parseRevenueMonthDetail,
  parseRevenueMonthsPage,
  parseRevenueProductsPage,
  parseSeriesResponse,
} from './parse';
import type {
  MemberState,
  MembershipFamiliesQuery,
  MembershipFamiliesResponse,
  MembershipMembersPage,
  MembershipMonthDetail,
  MembershipMonthsPage,
  RevenueLinesPage,
  RevenueMembersPage,
  RevenueMonthDetail,
  RevenueMonthsPage,
  RevenueProductsPage,
  SeriesGranularity,
  SeriesResponse,
} from './types';

const UNMAPPED_FAMILY_PATH = '__unmapped__';

/** Path token for the Unmapped family. Never build this token inline. */
export function familySlugToPath(slug: string | null): string {
  return slug ?? UNMAPPED_FAMILY_PATH;
}

/** Inverse of {@link familySlugToPath}. */
export function pathToFamilySlug(token: string): string | null {
  return token === UNMAPPED_FAMILY_PATH ? null : token;
}

export interface ReportParams {
  dimension?: string | null;
  currency?: string | null;
}

/** Appends the non-empty params to href (query-safe). Replaces appendDimension. */
export function appendReportParams(href: string, p: ReportParams): string {
  let result = href;
  if (p.dimension != null && p.dimension !== '') {
    result = `${result}${result.includes('?') ? '&' : '?'}dimension=${encodeURIComponent(p.dimension)}`;
  }
  if (p.currency != null && p.currency !== '') {
    result = `${result}${result.includes('?') ? '&' : '?'}currency=${encodeURIComponent(p.currency)}`;
  }
  return result;
}

/** Append `?dimension=` (or `&dimension=`) as the last query key. Empty/null is a no-op. */
export function appendDimension(href: string, dimension: string | null): string {
  return appendReportParams(href, { dimension });
}

/** True when the backend envelope code (not `ApiError.code`) is `DIMENSION_NOT_FOUND`. */
export function isDimensionNotFound(err: unknown): boolean {
  return err instanceof ApiError && err.details?.['code'] === 'DIMENSION_NOT_FOUND';
}

/** Backend envelope code `REPORT_CURRENCY_NOT_FOUND` (details['code']). */
export function isCurrencyNotFound(err: unknown): boolean {
  return err instanceof ApiError && err.details?.['code'] === 'REPORT_CURRENCY_NOT_FOUND';
}

/** Backend envelope code `INVALID_DATE_RANGE` (details['code']). */
export function isInvalidDateRange(err: unknown): boolean {
  return err instanceof ApiError && err.details?.['code'] === 'INVALID_DATE_RANGE';
}

export interface MembershipMonthsQuery {
  from: string;
  to: string;
  page: number;
  size: number;
}

export interface MembershipMembersQuery {
  state: MemberState; // REQUIRED by the API — always send it
  band: string; // '' omits the key
  search: string; // '' omits the key
  sort: string; // '' omits the key → server default
  order: 'asc' | 'desc';
  page: number;
  size: number;
}

function omitEmpty(params: Record<string, string | number>): Record<string, string | number> {
  const out: Record<string, string | number> = {};
  for (const [key, value] of Object.entries(params)) {
    if (typeof value === 'string' && value.trim().length === 0) continue;
    out[key] = typeof value === 'string' ? value.trim() : value;
  }
  return out;
}

export function fetchMembershipMonths(q: MembershipMonthsQuery): Promise<MembershipMonthsPage> {
  return request<unknown>('GET', '/admin/reports/membership/months', {
    query: omitEmpty({ from: q.from, to: q.to, page: q.page, size: q.size }),
  }).then(parseMembershipMonthsPage);
}

export function fetchMembershipMonth(month: string): Promise<MembershipMonthDetail> {
  return request<unknown>(
    'GET',
    `/admin/reports/membership/months/${encodeURIComponent(month)}`,
  ).then(parseMembershipMonthDetail);
}

/** `GET /admin/reports/membership/families` (WSRADM-0052 / WSRAPI-0158 B). Bearer only, omit empty params. */
export function fetchMembershipFamilies(
  q: MembershipFamiliesQuery = {},
): Promise<MembershipFamiliesResponse> {
  return request<unknown>('GET', '/admin/reports/membership/families', {
    query: omitEmpty({
      dimension: q.dimension ?? '',
      from: q.from ?? '',
      to: q.to ?? '',
    }),
  }).then(parseMembershipFamiliesResponse);
}

export function fetchMembershipMembers(
  month: string,
  q: MembershipMembersQuery,
): Promise<MembershipMembersPage> {
  const query: Record<string, string | number> = {
    state: q.state,
    page: q.page,
    size: q.size,
  };
  const band = q.band.trim();
  if (band.length > 0) query['band'] = band;
  const search = q.search.trim();
  if (search.length > 0) query['search'] = search;
  const sort = q.sort.trim();
  if (sort.length > 0) {
    query['sort'] = sort;
    query['order'] = q.order;
  }
  return request<unknown>(
    'GET',
    `/admin/reports/membership/months/${encodeURIComponent(month)}/members`,
    { query },
  ).then(parseMembershipMembersPage);
}

export function rebuildReports(): Promise<TriggerJobResponse | null> {
  return request<unknown>('POST', '/admin/reports/rebuild').then(parseTriggerJobResponse);
}

export interface RevenueMonthsQuery {
  currency: string; // '' omits the key — never pick a default
  dimension: string; // '' omits the key — never pick a default
  page: number;
  size: number;
}

export interface RevenueFamilyListQuery {
  currency: string;
  dimension: string;
  search: string;
  sort: string;
  order: 'asc' | 'desc';
  page: number;
  size: number;
}

export interface RevenueLinesQuery {
  currency: string;
  dimension: string;
  productKey: string; // '' omits the key
  sort: string;
  order: 'asc' | 'desc';
  page: number;
  size: number;
}

function familyPath(month: string, familySlug: string | null): string {
  return (
    `/admin/reports/revenue/months/${encodeURIComponent(month)}` +
    `/families/${encodeURIComponent(familySlugToPath(familySlug))}`
  );
}

export function fetchRevenueMonths(q: RevenueMonthsQuery): Promise<RevenueMonthsPage> {
  return request<unknown>('GET', '/admin/reports/revenue/months', {
    query: omitEmpty({ currency: q.currency, dimension: q.dimension, page: q.page, size: q.size }),
  }).then(parseRevenueMonthsPage);
}

export function fetchRevenueMonth(
  month: string,
  opts: { currency?: string; dimension?: string } = {},
): Promise<RevenueMonthDetail> {
  return request<unknown>(
    'GET',
    `/admin/reports/revenue/months/${encodeURIComponent(month)}`,
    { query: omitEmpty({ currency: opts.currency ?? '', dimension: opts.dimension ?? '' }) },
  ).then(parseRevenueMonthDetail);
}

export function fetchRevenueProducts(
  month: string,
  familySlug: string | null,
  q: RevenueFamilyListQuery,
): Promise<RevenueProductsPage> {
  return request<unknown>('GET', `${familyPath(month, familySlug)}/products`, {
    query: omitEmpty({
      currency: q.currency,
      dimension: q.dimension,
      search: q.search,
      sort: q.sort,
      order: q.sort.trim().length > 0 ? q.order : '',
      page: q.page,
      size: q.size,
    }),
  }).then(parseRevenueProductsPage);
}

export function fetchRevenueMembers(
  month: string,
  familySlug: string | null,
  q: RevenueFamilyListQuery,
): Promise<RevenueMembersPage> {
  return request<unknown>('GET', `${familyPath(month, familySlug)}/members`, {
    query: omitEmpty({
      currency: q.currency,
      dimension: q.dimension,
      search: q.search,
      sort: q.sort,
      order: q.sort.trim().length > 0 ? q.order : '',
      page: q.page,
      size: q.size,
    }),
  }).then(parseRevenueMembersPage);
}

export function fetchRevenueLines(
  month: string,
  familySlug: string | null,
  q: RevenueLinesQuery,
): Promise<RevenueLinesPage> {
  return request<unknown>('GET', `${familyPath(month, familySlug)}/lines`, {
    query: omitEmpty({
      currency: q.currency,
      dimension: q.dimension,
      productKey: q.productKey,
      sort: q.sort,
      order: q.sort.trim().length > 0 ? q.order : '',
      page: q.page,
      size: q.size,
    }),
  }).then(parseRevenueLinesPage);
}

export interface SeriesQuery {
  from: string;
  to: string;
  granularity: SeriesGranularity;
  dimension: string; // '' omits
  currency: string; // '' omits
}

/** `GET /admin/reports/series` — bearer only; omit empty params (WSRADM-0037). */
export function fetchSeries(q: SeriesQuery): Promise<SeriesResponse> {
  return request<unknown>('GET', '/admin/reports/series', {
    query: omitEmpty({
      from: q.from,
      to: q.to,
      granularity: q.granularity,
      dimension: q.dimension,
      currency: q.currency,
    }),
  }).then(parseSeriesResponse);
}
